<script setup lang="ts">
import BoxComponent from '../atoms/BoxComponent.vue'
import FilterButtonComponent from '../atoms/FilterButtonComponent.vue'
import FlexComponent from '../atoms/FlexComponent.vue'
import LoadingComponent from '../atoms/LoadingComponent.vue'
import TooltipComponent from '../atoms/TooltipComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'

import DateRangePicker from './DateRangePicker.vue'
import LineChartComponent from './LineChartComponent.vue'

type TooltipBreakdownEntry = { platform: string; label: string; formatted: string | number }
type ChartDataset = { x: string; y: string | number; longAmount: string | number; valueUnit?: string; breakdown?: TooltipBreakdownEntry[] }
type PerformanceChartButton = { value: string; label: string; shortAmount: string; longAmount: string; amount: number }

withDefaults(
  defineProps<{
    title?: string
    buttons: PerformanceChartButton[]
    activeFilterTarget: string
    isLoading: boolean
    isChartLoading: boolean
    chartData: ChartDataset[]
    formatYAxisLabel?: (value: number) => string
    maxDateRangeDays?: number
  }>(),
  {
    title: 'Campaign performance',
  },
)
const dateRange = defineModel<{ startDate: string; endDate: string }>('dateRange')
const emit = defineEmits<(e: 'filterClick', value: string) => void>()
</script>
<template>
  <BoxComponent w="100%" :mb="24">
    <FlexComponent justify="space-between" align="center" wrap="wrap">
      <TypographyComponent variant="headline-small">{{ title }}</TypographyComponent>
      <DateRangePicker v-model="dateRange" :max-days="maxDateRangeDays" />
    </FlexComponent>
    <LoadingComponent v-if="isLoading" :size="48" class="campaign-performance-chart__loader" />
    <BoxComponent v-else>
      <BoxComponent :mt="24" :mb="16">
        <FlexComponent wrap="wrap" :gap="4">
          <TooltipComponent
            v-for="item of buttons"
            :key="item.value"
            :disabled="!item.amount || item.shortAmount === item.longAmount"
            class="campaign-performance-chart__tooltip-wrapper"
          >
            <template #default>
              <FilterButtonComponent
                :value="item.value"
                :is-active="item.value === activeFilterTarget"
                class="campaign-performance-chart__filter-button"
                @on-click="(value) => emit('filterClick', value as string)"
              >
                <template #label>
                  <span :class="{ 'campaign-performance-chart__button-active': item.value === activeFilterTarget }">
                    {{ item.label }}
                  </span>
                </template>
                <template #count>{{ item.shortAmount }}</template>
              </FilterButtonComponent>
            </template>
            <template #content>
              <TypographyComponent variant="body-small">{{ item.longAmount }}</TypographyComponent>
            </template>
          </TooltipComponent>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent :h="264">
        <FlexComponent v-if="isChartLoading" align="center" justify="center" class="campaign-performance-chart__chart-loader">
          <LoadingComponent :size="48" />
        </FlexComponent>
        <LineChartComponent v-else :data="chartData" :format-y-axis-label="formatYAxisLabel" />
      </BoxComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.campaign-performance-chart__tooltip-wrapper {
  width: calc(25% - 8px);
}
.campaign-performance-chart__filter-button {
  width: 100%;
}
.campaign-performance-chart__button-active {
  position: relative;

  &:before {
    content: ' ';
    position: absolute;
    top: 3px;
    left: -12px;
    background-color: #349fd6;
    border-radius: 50%;
    width: 8px;
    height: 8px;
    display: inline-block;
  }
}
.campaign-performance-chart__loader {
  height: 400px;
}
.campaign-performance-chart__chart-loader {
  height: 100%;
}
</style>
